import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import { 
  FilterAlt as FilterIcon,
  Search as SearchIcon,
  SaveAlt as SaveIcon,
  Refresh as RefreshIcon,
  ArrowDownward as ReceiveIcon,
  ArrowUpward as SendIcon,
  SwapHoriz as SwapIcon,
  ShoppingCart as ShoppingCartIcon,
  DateRange as DateRangeIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { formatDistanceToNow } from 'date-fns';
import { tokenWalletService } from '../../../services/skillcoinService';

const TokenHistoryExplorer = ({ initialTransactions = null, initialStats = null, forceRefresh = 0 }) => {
  const theme = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState(initialTransactions || []);
  const [loading, setLoading] = useState(!initialTransactions);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [metrics, setMetrics] = useState({
    totalEarned: 0,
    totalSpent: 0,
    netBalance: 0,
    totalTransactions: 0
  });
  
  // Filter state
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    startDate: null,
    endDate: null,
    minAmount: '',
    maxAmount: '',
    searchTerm: ''
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [isFiltered, setIsFiltered] = useState(false);
  
  // Force a refresh when the forceRefresh prop changes
  useEffect(() => {
    if (forceRefresh > 0) {
      console.log('Forced refresh triggered with key:', forceRefresh);
      fetchTransactions(true);
      fetchAllTransactions(true);
    }
  }, [forceRefresh]);

  // Initialize with data if provided as props
  useEffect(() => {
    if (initialTransactions && initialTransactions.length > 0) {
      setAllTransactions(initialTransactions);
      setTransactions(initialTransactions.slice(0, rowsPerPage));
      setTotalCount(initialTransactions.length);
      setLoading(false);
    } else {
      fetchTransactions();
      fetchAllTransactions();
    }
  }, []);

  // Apply pagination when all transactions change
  useEffect(() => {
    if (allTransactions.length > 0) {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setTransactions(allTransactions.slice(startIndex, endIndex));
      setTotalCount(allTransactions.length);
    }
  }, [page, rowsPerPage, allTransactions]);

  // When active filters change, filter the transactions
  useEffect(() => {
    if (allTransactions.length > 0 && Object.keys(activeFilters).length > 0) {
      const filteredTransactions = allTransactions.filter(tx => {
        // Filter by transaction type
        if (activeFilters.type && activeFilters.type !== 'all' && tx.type !== activeFilters.type) {
          return false;
        }
        
        // Filter by search term in description
        if (activeFilters.searchTerm && 
            !tx.description?.toLowerCase().includes(activeFilters.searchTerm.toLowerCase())) {
          return false;
        }
        
        // Filter by date range
        if (activeFilters.startDate) {
          const txDate = new Date(tx.date || tx.createdAt);
          const startDate = new Date(activeFilters.startDate);
          if (txDate < startDate) return false;
        }
        
        if (activeFilters.endDate) {
          const txDate = new Date(tx.date || tx.createdAt);
          const endDate = new Date(activeFilters.endDate);
          if (txDate > endDate) return false;
        }
        
        // Filter by amount range
        if (activeFilters.minAmount && parseFloat(tx.amount) < parseFloat(activeFilters.minAmount)) {
          return false;
        }
        
        if (activeFilters.maxAmount && parseFloat(tx.amount) > parseFloat(activeFilters.maxAmount)) {
          return false;
        }
        
        return true;
      });
      
      setTotalCount(filteredTransactions.length);
      setTransactions(filteredTransactions.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage));
    }
  }, [activeFilters, page, rowsPerPage]);

  // Calculate metrics based on all transactions
  const calculatedMetrics = useMemo(() => {
    // If initialStats provided, prioritize those
    if (initialStats) {
      return {
        totalEarned: initialStats.earned || initialStats.totalEarned || 0,
        totalSpent: initialStats.spent || initialStats.totalSpent || 0,
        netBalance: (initialStats.netBalance !== undefined) 
          ? initialStats.netBalance 
          : ((initialStats.earned || initialStats.totalEarned || 0) - 
             (initialStats.spent || initialStats.totalSpent || 0)),
        totalTransactions: initialStats.totalTransactions || allTransactions.length || 0
      };
    }
    
    if (!Array.isArray(allTransactions) || allTransactions.length === 0) {
      return {
        totalEarned: 0,
        totalSpent: 0,
        netBalance: 0,
        totalTransactions: allTransactions.length || 0
      };
    }
    
    return allTransactions.reduce((acc, tx) => {
      const amount = parseFloat(tx.amount) || 0;
      const type = (tx.type || '').toLowerCase();
      
      if (type === 'earn' || type === 'transfer_in') {
        acc.totalEarned += amount;
      } else if (type === 'spend' || type === 'transfer_out') {
        acc.totalSpent += amount;
      }
      
      acc.netBalance = acc.totalEarned - acc.totalSpent;
      acc.totalTransactions = allTransactions.length;
      
      return acc;
    }, { totalEarned: 0, totalSpent: 0, netBalance: 0, totalTransactions: 0 });
  }, [allTransactions, initialStats]);

  // Update metrics display with calculated values
  useEffect(() => {
    setMetrics(calculatedMetrics);
  }, [calculatedMetrics]);

  // Fetch all transactions without pagination for metric calculations
  const fetchAllTransactions = async (forceFresh = false) => {
    try {
      // Generate a unique cache buster when forced refresh is needed
      const cacheBuster = forceFresh ? `_t=${new Date().getTime()}` : '';
      
      // Fetch without pagination to get all transactions
      const response = await tokenWalletService.getTransactions(null, { limit: 1000 }, cacheBuster);
      let transactionsData = [];
      
      if (response && response.success && response.data) {
        transactionsData = response.data;
      } else if (Array.isArray(response)) {
        transactionsData = response;
      } else if (response && Array.isArray(response.data)) {
        transactionsData = response.data;
      }
      
      // Filter out duplicate transactions with the same description and close timestamps
      const uniqueTransactions = removeDuplicateTransactions(transactionsData);
      setAllTransactions(uniqueTransactions);
    } catch (err) {
      console.error('Error fetching all transactions for metrics:', err);
    }
  };

  // Helper function to remove duplicate transactions from different sources (Wallet and Submissions)
  const removeDuplicateTransactions = (transactions) => {
    if (!Array.isArray(transactions) || transactions.length === 0) return [];
    
    const uniqueTransactions = [];
    const duplicateTracker = new Map();
    
    // Sort transactions by date descending
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
    );
    
    // First pass: Group transactions by their core properties
    sortedTransactions.forEach(transaction => {
      // For "Reward for completing" transactions, extract the actual activity name
      let baseDescription = '';
      const originalDesc = (transaction.description || '').trim();
      
      if (originalDesc.toLowerCase().includes('reward for completing')) {
        baseDescription = originalDesc.replace(/reward for completing:?\s*/i, '').trim();
      } else {
        baseDescription = originalDesc;
      }
      
      // Clean the description for better matching
      baseDescription = baseDescription.toLowerCase();
      
      // Extract date components for time-window matching
      const dateObj = new Date(transaction.createdAt || transaction.date);
      const dateString = `${dateObj.getFullYear()}-${dateObj.getMonth()+1}-${dateObj.getDate()}`;
      const hourMinute = `${dateObj.getHours()}:${dateObj.getMinutes()}`;
      
      // Check if this is a wallet transaction and another source exists
      const source = (transaction.metadata?.source || transaction.source || '').toLowerCase();
      
      // Skip if manual credit, completely removing this functionality
      const isManualCredit = (
        originalDesc.toLowerCase().includes('manual credit') ||
        originalDesc.toLowerCase().includes('manualcredit') ||
        originalDesc.toLowerCase().includes('credit adjustment') ||
        (transaction.description && transaction.description.toLowerCase().includes('manual')) ||
        (transaction.metadata && transaction.metadata.isManual)
      );
      
      // Skip this specific reward transaction
      const isUnwantedReward = (
        originalDesc.toLowerCase().includes('shop membasuite merch on target')
      );
      
      if (isManualCredit || isUnwantedReward) {
        return; // Skip this transaction
      }
      
      // Create a unique group key based on the activity name and date only, ignoring source
      // This will group the same activity from different sources (wallet, submission) together
      const groupKey = `${baseDescription}_${dateString}_${transaction.amount}`;
      
      // Add transaction to group or create new group
      if (!duplicateTracker.has(groupKey)) {
        duplicateTracker.set(groupKey, []);
      }
      
      duplicateTracker.get(groupKey).push({
        transaction,
        dateTime: dateObj,
        hourMinute,
        originalDesc,
        source
      });
    });
    
    // Second pass: Process each group to select the transaction to keep
    duplicateTracker.forEach((group, key) => {
      if (group.length === 1) {
        // If only one transaction in the group, keep it
        uniqueTransactions.push(group[0].transaction);
        return;
      }
      
      // If multiple transactions in the same time window, need to decide which to keep
      
      // Check for time proximity (transactions within 10 minutes of each other)
      const isTimeProximity = group.some((item, i) => {
        if (i === 0) return false;
        
        const timeDiff = Math.abs(item.dateTime - group[0].dateTime);
        return timeDiff < 10 * 60 * 1000; // 10 minutes in milliseconds
      });
      
      if (isTimeProximity) {
        // If transactions are close in time, they're likely duplicates
        
        // For submissions and wallet entries of the same activity, prefer the submission entry
        // First, check if we have both wallet and non-wallet sources
        const hasWalletSource = group.some(item => 
          item.source === 'wallet' || item.source.includes('wallet')
        );
        
        const hasNonWalletSource = group.some(item => 
          item.source !== 'wallet' && !item.source.includes('wallet') && 
          item.source !== '' && item.source !== 'undefined'
        );
        
        // If we have both types, prefer the non-wallet source
        if (hasWalletSource && hasNonWalletSource) {
          const nonWalletItems = group.filter(item => 
            item.source !== 'wallet' && !item.source.includes('wallet')
          );
          
          // From non-wallet items, prefer the one with the cleanest description
          const bestTransaction = nonWalletItems.reduce((best, current) => {
            // Prefer transactions without "Reward for completing" prefix
            if (!best.originalDesc.toLowerCase().includes('reward for completing') && 
                current.originalDesc.toLowerCase().includes('reward for completing')) {
              return best;
            }
            
            if (best.originalDesc.toLowerCase().includes('reward for completing') && 
                !current.originalDesc.toLowerCase().includes('reward for completing')) {
              return current;
            }
            
            // If both have the same prefix status, prefer the one with more descriptive text
            return current.originalDesc.length > best.originalDesc.length ? current : best;
          }, nonWalletItems[0]);
          
          uniqueTransactions.push(bestTransaction.transaction);
          
          console.log(
            `Detected duplicate transactions "${key}". Keeping non-wallet source. Source: "${bestTransaction.source}" Description: "${bestTransaction.originalDesc}"`
          );
          
          return;
        }
        
        // If no mix of wallet/non-wallet, prefer transactions without "Reward for completing" prefix
        const withoutPrefix = group.find(item => 
          !item.originalDesc.toLowerCase().includes('reward for completing')
        );
        
        if (withoutPrefix) {
          uniqueTransactions.push(withoutPrefix.transaction);
          return;
        }
        
        // As a last resort, prefer the one with the most descriptive name
        const bestTransaction = group.reduce((best, current) => 
          current.originalDesc.length > best.originalDesc.length ? current : best
        , group[0]);
        
        uniqueTransactions.push(bestTransaction.transaction);
        return;
      } else {
        // If the transactions are not close in time, keep them all
        group.forEach(item => {
          uniqueTransactions.push(item.transaction);
        });
      }
    });
    
    return uniqueTransactions;
  };

  const fetchTransactions = async (forceFresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Generate a unique cache buster when forced refresh is needed
      const cacheBuster = forceFresh ? `_t=${new Date().getTime()}` : '';
      
      console.log('Fetching transactions with cache buster:', cacheBuster);
      
      const response = await tokenWalletService.getHistoricalTokenTransactions(
        activeFilters,
        page + 1,
        rowsPerPage,
        cacheBuster
      );
      
      if (response.success) {
        setTransactions(response.data || []);
        setTotalCount(response.pagination?.totalItems || 0);
      } else {
        setError(response.error || 'Failed to fetch transaction history');
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching token history:', err);
      setError(err.message || 'An error occurred while fetching transaction history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredTransactions = async (forceFresh = false) => {
    setLoading(true);
    setError(null);
    try {
      // Generate a unique cache buster when forced refresh is needed
      const cacheBuster = forceFresh ? `_t=${new Date().getTime()}` : '';
      
      const response = await tokenWalletService.getHistoricalTokenTransactions(
        activeFilters,
        page + 1,
        rowsPerPage,
        cacheBuster
      );
      
      if (response.success) {
        setTransactions(response.data || []);
        setTotalCount(response.pagination?.totalItems || 0);
      } else {
        setError(response.error || 'Failed to fetch transaction history');
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching token history:', err);
      setError(err.message || 'An error occurred while fetching transaction history');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenFilterDialog = () => {
    setFilterDialogOpen(true);
  };

  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    // Convert dates to ISO strings if they exist
    const formattedFilters = {
      ...filters,
      startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
      endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null
    };
    
    // Remove empty filters
    const cleanedFilters = Object.entries(formattedFilters).reduce((acc, [key, value]) => {
      if (value !== null && value !== '' && value !== 'all') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    setActiveFilters(cleanedFilters);
    setIsFiltered(Object.keys(cleanedFilters).length > 0);
    setPage(0);
    setFilterDialogOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      type: 'all',
      startDate: null,
      endDate: null,
      minAmount: '',
      maxAmount: '',
      searchTerm: ''
    });
    setActiveFilters({});
    setIsFiltered(false);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchTransactions(true);
    fetchAllTransactions(true);
  };

  const exportTransactions = (format) => {
    if (transactions.length === 0) return;
    
    let content;
    let filename;
    let type;
    
    if (format === 'csv') {
      // Create CSV content
      const headers = ["Type", "Amount", "Description", "Date", "Status"];
      const rows = transactions.map(tx => [
        tx.type || '',
        tx.amount || 0,
        tx.description || '',
        new Date(tx.createdAt || tx.date).toLocaleString(),
        tx.status || 'completed'
      ]);
      
      content = [headers, ...rows].map(row => row.join(',')).join('\n');
      filename = `token-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      type = 'text/csv';
    } else {
      // Create JSON content
      content = JSON.stringify(transactions, null, 2);
      filename = `token-transactions-${new Date().toISOString().slice(0, 10)}.json`;
      type = 'application/json';
    }
    
    // Create download link
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Return full date and time
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (transaction) => {
    const type = transaction.type?.toLowerCase() || 'unknown';
    
    if (type === 'earn') {
      return <ReceiveIcon sx={{ color: theme.palette.success.main }} />;
    } else if (type === 'spend') {
      return <ShoppingCartIcon sx={{ color: theme.palette.error.main }} />;
    } else if (type === 'transfer_out') {
      return <SendIcon sx={{ color: theme.palette.warning.main }} />;
    } else if (type === 'transfer_in') {
      return <ReceiveIcon sx={{ color: theme.palette.success.main }} />;
    } else {
      return <SwapIcon />;
    }
  };
  
  const getTransactionTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'earn':
        return 'Earned';
      case 'spend':
        return 'Spent';
      case 'transfer_in':
        return 'Received';
      case 'transfer_out':
        return 'Sent';
      default:
        return type || 'Unknown';
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'earn':
      case 'transfer_in':
        return 'success';
      case 'spend':
        return 'error';
      case 'transfer_out':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Token Transaction History
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        View and search your complete token transaction history
      </Typography>
      
      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mt: 1, mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: theme.shape.borderRadius * 2
          }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Earned
              </Typography>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {metrics.totalEarned.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(145deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.error.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: theme.shape.borderRadius * 2
          }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Spent
              </Typography>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {metrics.totalSpent.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: theme.shape.borderRadius * 2
          }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Net Balance
              </Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {metrics.netBalance.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            borderRadius: theme.shape.borderRadius * 2
          }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {metrics.totalTransactions.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: theme.shape.borderRadius * 1.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={handleOpenFilterDialog}
                sx={{ borderRadius: 2 }}
              >
                Filter Transactions
              </Button>
              
              {isFiltered && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ borderRadius: 2 }}
                >
                  Clear Filters
                </Button>
              )}
              
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Stack>
            
            {isFiltered && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Active filters:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {Object.entries(activeFilters).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value}`}
                      size="small"
                      onDelete={() => {
                        const newFilters = { ...activeFilters };
                        delete newFilters[key];
                        setActiveFilters(newFilters);
                        setIsFiltered(Object.keys(newFilters).length > 0);
                      }}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<SaveIcon />}
                onClick={() => exportTransactions('csv')}
                disabled={transactions.length === 0}
                sx={{ borderRadius: 2 }}
              >
                Export CSV
              </Button>
              
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<SaveIcon />}
                onClick={() => exportTransactions('json')}
                disabled={transactions.length === 0}
                sx={{ borderRadius: 2 }}
              >
                Export JSON
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Transaction Table */}
      <Paper sx={{ 
        width: '100%',
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius * 1.5,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
      }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Loading transactions...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1">
                      {isFiltered
                        ? 'No transactions match your filter criteria'
                        : 'No transaction history found'}
                    </Typography>
                    {isFiltered && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={clearFilters}
                        sx={{ mt: 1 }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction, index) => (
                  <TableRow
                    key={transaction._id || index}
                    hover
                    sx={{
                      '&:nth-of-type(even)': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getTransactionIcon(transaction)}
                        <Chip
                          label={getTransactionTypeLabel(transaction.type)}
                          color={getTransactionTypeColor(transaction.type)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: transaction.type?.toLowerCase().includes('earn') || 
                                 transaction.type?.toLowerCase().includes('transfer_in')
                            ? 'success.main'
                            : 'error.main'
                        }}
                      >
                        {(transaction.type?.toLowerCase().includes('earn') || 
                          transaction.type?.toLowerCase().includes('transfer_in')
                          ? '+'
                          : '-'
                        )}
                        {parseFloat(transaction.amount).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDate(transaction.createdAt || transaction.date)}>
                        <Typography variant="body2">
                          {formatDistanceToNow(new Date(transaction.createdAt || transaction.date), { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.metadata?.source || 'system'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={handleCloseFilterDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: theme.shape.borderRadius * 1.5 }
        }}
      >
        <DialogTitle>Filter Transactions</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
                <Select
                  labelId="transaction-type-label"
                  id="transaction-type"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  label="Transaction Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="earn">Earned</MenuItem>
                  <MenuItem value="spend">Spent</MenuItem>
                  <MenuItem value="transfer_in">Received</MenuItem>
                  <MenuItem value="transfer_out">Sent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search in descriptions"
                variant="outlined"
                margin="normal"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  maxDate={filters.endDate || undefined}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  minDate={filters.startDate || undefined}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Amount"
                variant="outlined"
                margin="normal"
                type="number"
                value={filters.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maximum Amount"
                variant="outlined"
                margin="normal"
                type="number"
                value={filters.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilterDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={clearFilters} color="inherit" startIcon={<ClearIcon />}>
            Clear All
          </Button>
          <Button onClick={applyFilters} color="primary" variant="contained" startIcon={<FilterIcon />}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TokenHistoryExplorer;